import React, { useState } from "react"
import css from "@styled-system/css"
import { AnimatePresence, motion } from "framer-motion"
import { ellipsis } from "polished"

import { Box, Flex, Heading, Image } from "components"

import Spacer from "./spacer"

function Gallery({
  isMobile,
  location,
  project,
  position,
  opacity,
  hideHeader,
  topSpace,
  bottomSpace,
}) {
  const [showVideo, setShowVideo] = useState(false)

  return (
    <motion.div
      variants={
        !isMobile
          ? {
              visible: {
                x: 0,
                transition: {
                  delay:
                    location.search.indexOf("?scroll=") !== -1 ||
                    location.search.indexOf("?topScroll=") !== -1
                      ? 0
                      : 0.5,
                  duration:
                    location.search.indexOf("?scroll=") !== -1 ||
                    location.search.indexOf("?topScroll=") !== -1
                      ? 0
                      : 1.2,
                  ease: [0.215, 0.61, 0.355, 1],
                },
              },
              initial: {
                x: 1200,
                transition: {
                  when: "afterChildren",
                },
              },
            }
          : {
              visible: {
                opacity: 1,
                transition: {
                  delay:
                    location.search.indexOf("?scroll=") !== -1 ||
                    location.search.indexOf("?topScroll=") !== -1
                      ? 0
                      : 0.5,
                  duration:
                    location.search.indexOf("?scroll=") !== -1 ||
                    location.search.indexOf("?topScroll=") !== -1
                      ? 0
                      : 0.6,
                  ease: [0.215, 0.61, 0.355, 1],
                },
              },
              initial: {
                opacity: 0,
                transition: {
                  when: "afterChildren",
                },
              },
            }
      }
      initial="initial"
      animate="visible"
      key={project.title + "gallery"}
      style={{ minWidth: "100%" }}
    >
      <Flex
        position="relative"
        flexDirection={["column", null, "row"]}
        width={["100%", null, "auto"]}
      >
        {!hideHeader && (
          <Flex
            alignItems="flex-start"
            height={[48, topSpace]}
            pt={[16, 32]}
            pr={16}
            css={css({
              position: ["fixed", null, position],
              zIndex: 10,
              bg: "background",
              top: [0, null, position === "fixed" ? 0 : "-" + topSpace],
              left: [48, 80, `${position === "fixed" ? 80 : 0} !important`],
              right: [0, 0, null],
            })}
            style={{
              opacity: opacity,
              overflow: "hidden",
            }}
          >
            <Heading
              as="div"
              size={100}
              style={{
                ...ellipsis(),
              }}
            >
              Par
              <Box
                as="span"
                display="inline-block"
                css={{ transform: "scale(-1, 1)" }}
              >
                k
              </Box>
              {project && project.category && (
                <>
                  {" × " + project.title + " "}
                  <Box as="span" fontFamily="serif" fontWeight={300}>
                    {project.category}
                  </Box>
                </>
              )}
            </Heading>
          </Flex>
        )}
        <motion.div
          variants={
            isMobile
              ? {
                  visible: {
                    y: 0,
                    transition: {
                      delay:
                        location.search.indexOf("?scroll=") !== -1 ||
                        location.search.indexOf("?topScroll=") !== -1
                          ? 0
                          : 0.5,
                      duration:
                        location.search.indexOf("?scroll=") !== -1 ||
                        location.search.indexOf("?topScroll=") !== -1
                          ? 0
                          : 1.2,
                      ease: [0.215, 0.61, 0.355, 1],
                    },
                  },
                  initial: {
                    y: 600,
                    transition: {
                      when: "afterChildren",
                    },
                  },
                }
              : {}
          }
          initial="initial"
          animate="visible"
          exit="exit"
          key={project.title + "galleryInner"}
        >
          <Flex flexDirection={["column", null, "row"]}>
            {project.featuredVideo &&
              project.featuredVideo.url &&
              project.featuredVideo.height &&
              project.featuredVideo.width && (
                <>
                  <Box
                    data-component-id="🎥"
                    position="relative"
                    minWidth={[
                      "100%",
                      null,
                      `calc(${project.featuredVideo.width /
                        project.featuredVideo
                          .height} * (100vh - ${topSpace} - ${bottomSpace}))`,
                    ]}
                    height={[
                      `calc(${(project.featuredVideo.height /
                        project.featuredVideo.width) *
                        100}vw)`,
                      null,
                      `calc(100vh - ${topSpace} - ${bottomSpace})`,
                    ]}
                    bg="black"
                  >
                    <AnimatePresence>
                      {!showVideo && (
                        <motion.div
                          position="absolute"
                          top={0}
                          left={0}
                          width="100%"
                          height="100%"
                          key="video"
                          variants={{
                            visible: {
                              opacity: 1,
                              transition: {
                                ease: [0.215, 0.61, 0.355, 1],
                              },
                            },
                            initial: {
                              opacity: 0,
                              transition: {
                                ease: [0.215, 0.61, 0.355, 1],
                              },
                            },
                            exit: {
                              opacity: 0,
                              transition: {
                                ease: [0.215, 0.61, 0.355, 1],
                              },
                            },
                          }}
                          initial="initial"
                          animate="visible"
                          exit="exit"
                        >
                          <video
                            width="100%"
                            height="100%"
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            <source
                              src={project.featuredVideo.url}
                              type="video/mp4"
                            />
                          </video>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <AnimatePresence>
                      {showVideo &&
                        project.featuredVimeo &&
                        project.featuredVimeo.url && (
                          <motion.div
                            position="absolute"
                            top={0}
                            left={0}
                            height="100%"
                            width="100%"
                            variants={{
                              visible: {
                                opacity: 1,
                                transition: {
                                  ease: [0.215, 0.61, 0.355, 1],
                                },
                              },
                              initial: {
                                opacity: 0,
                                transition: {
                                  ease: [0.215, 0.61, 0.355, 1],
                                },
                              },
                              exit: {
                                opacity: 0,
                                transition: {
                                  ease: [0.215, 0.61, 0.355, 1],
                                },
                              },
                            }}
                            initial="initial"
                            animate="visible"
                            exit="exit"
                            key="vimeo"
                            style={{ backgroundColor: "black" }}
                          >
                            <Box
                              as="iframe"
                              position="absolute"
                              top={0}
                              left={0}
                              height="100%"
                              width="100%"
                              src={
                                "https://player.vimeo.com/video/" +
                                project.featuredVimeo.url.split("/")[
                                  project.featuredVimeo.url.split("/").length -
                                    1
                                ] +
                                "?autoplay=1"
                              }
                              title="Video player"
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              style={{ objectFit: "cover" }}
                            />
                          </motion.div>
                        )}
                    </AnimatePresence>
                    <AnimatePresence>
                      {!showVideo &&
                        project.featuredVimeo &&
                        project.featuredVimeo.url && (
                          <motion.button
                            position="absolute"
                            top={0}
                            left={0}
                            height="100%"
                            width="100%"
                            onClick={() => setShowVideo(true)}
                            key="overlay"
                            variants={{
                              visible: {
                                opacity: 1,
                                transition: {
                                  ease: [0.215, 0.61, 0.355, 1],
                                },
                              },
                              initial: {
                                opacity: 0,
                                transition: {
                                  ease: [0.215, 0.61, 0.355, 1],
                                },
                              },
                              exit: {
                                opacity: 0,
                                transition: {
                                  ease: [0.215, 0.61, 0.355, 1],
                                },
                              },
                            }}
                            initial="initial"
                            animate="visible"
                            exit="exit"
                          >
                            <Box
                              position="absolute"
                              top={0}
                              left={0}
                              height="100%"
                              width="100%"
                              bg="black"
                              opacity="0.4"
                            />
                            <Box
                              as="svg"
                              position="absolute"
                              top="50%"
                              left="50%"
                              height="100%"
                              width="100%"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width="96px"
                              height="96px"
                              style={{ transform: "translate(-50%, -50%)" }}
                            >
                              <path fill="none" d="M0 0h24v24H0z" />
                              <path
                                d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM10.622 8.415l4.879 3.252a.4.4 0 0 1 0 .666l-4.88 3.252a.4.4 0 0 1-.621-.332V8.747a.4.4 0 0 1 .622-.332z"
                                fill="white"
                              />
                            </Box>
                          </motion.button>
                        )}
                    </AnimatePresence>
                  </Box>

                  <Spacer />
                </>
              )}
            {project.gallery &&
              project.gallery.map(({ photo }, index) => {
                if (photo && photo.url) {
                  return (
                    <>
                      <Box
                        data-component-id="🖼"
                        position="relative"
                        minWidth={[
                          "100%",
                          null,
                          `calc(${photo.dimensions.width /
                            photo.dimensions
                              .height} * (100vh - ${topSpace} - ${bottomSpace}))`,
                        ]}
                        height={[
                          `calc(${(photo.dimensions.height /
                            photo.dimensions.width) *
                            100}vw)`,
                          null,
                          `calc(100vh - ${topSpace} - ${bottomSpace})`,
                        ]}
                        key={photo.url + index}
                      >
                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          width="100%"
                          height="100%"
                          bg="text"
                        >
                          <Image
                            image={{
                              url: photo.url + "&fit=fill&h=1700",
                              dimensions: photo.dimensions,
                              twoThirds: {
                                url: photo.url + "&fit=fill&h=1700",
                                dimensions: {
                                  height: photo.dimensions.height / 5,
                                  width: photo.dimensions.width / 5,
                                },
                              },
                              half: {
                                url: photo.url + "&fit=fill&h=600",
                                dimensions: {
                                  height: photo.dimensions.height / 7,
                                  width: photo.dimensions.width / 7,
                                },
                              },
                              placeholder: {
                                url: photo.url + "&fit=fill&h=16",
                                dimensions: {
                                  height: photo.dimensions.height / 10,
                                  width: photo.dimensions.width / 10,
                                },
                              },
                            }}
                            sizes="1200px"
                            style={{ height: "100%" }}
                          />
                        </Box>
                      </Box>

                      <Spacer />
                    </>
                  )
                } else {
                  return null
                }
              })}
          </Flex>
        </motion.div>
      </Flex>
    </motion.div>
  )
}

export default Gallery
